import {
  aircraftDetailQuery,
  allFittedComponentsQuery,
  modStatusInfiniteQuery,
} from "@common/api/aircraft"
import { ataChaptersQuery } from "@common/api/ata-chapter"
import { featureFlagsQuery, useFeatureFlag } from "@common/api/feature-flags"
import { currentUserQuery } from "@common/api/users"
import { DASHBOARD_AIRCRAFT, WO_PAGES } from "@common/flags"
import { useCurrentUser } from "@common/hooks/use-permissions"
import { hasPermission } from "@common/lib/permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Outlet, useRouterState } from "@tanstack/react-router"
import { FilterBar } from "@/components/layout/filter-bar"
import { requirePermission } from "@/lib/require-permission"
import { AircraftSummary } from "./-components/aircraft-summary"
import { AircraftTab } from "./-components/aircraft-tab"
import { AircraftTabs } from "./-components/aircraft-tabs"
import { FITTED_COMPONENT_ROUTES } from "./-lib/fitted-components"
import { MOD_STATUS_PANES, modStatusPagesParams } from "./-lib/mod-status"
import {
  aircraftPageSearchSchema,
  DEFAULT_TAB,
  type Tab,
  tabsFor,
} from "./-lib/tabs"
import { TECH_LOG_ROUTES } from "./-lib/use-update-tech-log-search"

export const Route = createFileRoute("/_layout/aircraft_/$aircraftId")({
  validateSearch: (search) => aircraftPageSearchSchema.parse(search),
  beforeLoad: ({ context: { queryClient } }) =>
    requirePermission(queryClient, "aircraft:read", "/"),
  preloadStaleTime: 0,
  loader: async ({
    context: { queryClient },
    params: { aircraftId },
    preload,
  }) => {
    void queryClient.prefetchQuery(featureFlagsQuery())
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (!preload && hasPermission(me, "fitted_components:read")) {
      void queryClient.prefetchQuery(allFittedComponentsQuery(aircraftId))
    }
    if (!preload && hasPermission(me, "mod_status:read")) {
      void queryClient.prefetchInfiniteQuery(
        modStatusInfiniteQuery(modStatusPagesParams(aircraftId, {})),
      )
    }
    const [aircraft] = await Promise.all([
      queryClient.ensureQueryData(aircraftDetailQuery({ id: aircraftId })),
      queryClient.ensureQueryData(ataChaptersQuery()),
    ])
    return {
      breadcrumb: [
        { label: "Aircraft", href: "/aircraft" },
        { label: aircraft.registration_no },
      ],
    }
  },
  component: AircraftPage,
})

function routeTab(leaf: string | undefined): Tab | undefined {
  if (leaf === undefined) return undefined
  if (TECH_LOG_ROUTES.includes(leaf)) return "tech-log"
  if (FITTED_COMPONENT_ROUTES.includes(leaf)) return "fitted-components"
  if (MOD_STATUS_PANES.includes(leaf)) return "modification-status"
  return undefined
}

function AircraftPage(): React.ReactElement {
  const { aircraftId } = Route.useParams()
  const search = Route.useSearch()
  const { data: aircraft } = useSuspenseQuery(
    aircraftDetailQuery({ id: aircraftId }),
  )
  const overview = useFeatureFlag(DASHBOARD_AIRCRAFT) ?? false
  const woPages = useFeatureFlag(WO_PAGES) ?? false
  const tabs = tabsFor(useCurrentUser(), overview, woPages)
  const leaf = useRouterState({
    select: (state) => state.matches.at(-1)?.routeId,
  })
  const picked = routeTab(leaf) ?? search.tab
  const tab = picked && tabs.includes(picked) ? picked : DEFAULT_TAB

  return (
    <div className="@container flex h-full min-h-0 flex-col">
      <FilterBar>
        <div className="flex h-8 items-center gap-3">
          <AircraftSummary aircraft={aircraft} />
        </div>
        <div className="mt-1 flex h-8 items-center gap-2">
          <AircraftTabs aircraftId={aircraft.id} tab={tab} tabs={tabs} />
        </div>
      </FilterBar>
      <div className="flex min-h-0 flex-1">
        <AircraftTab aircraft={aircraft} tab={tab} detail={<Outlet />} />
      </div>
    </div>
  )
}
